import React, { useState, useEffect } from 'react';
import { fetchAllData } from '../../helpers/externapi';
import * as XLSX from 'xlsx';

const DashboardLeaderBoardMonthlyHRMHAO = () => {
    const [leaderData, setLeaderData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [visibleCount, setVisibleCount] = useState(10);

    const PAGE_SIZE = 10;

    const today = new Date();
    const currentMonthIndex = today.getMonth();

    const allMonths = [
        { short: 'Jan', api: 'Jan' },
        { short: 'Feb', api: 'Feb' },
        { short: 'Mar', api: 'Mar' },
        { short: 'Apr', api: 'Apr' },
        { short: 'May', api: 'May' },
        { short: 'Jun', api: 'June' },
        { short: 'Jul', api: 'July' },
        { short: 'Aug', api: 'Aug' },
        { short: 'Sep', api: 'Sep' },
        { short: 'Oct', api: 'Oct' },
        { short: 'Nov', api: 'Nov' },
        { short: 'Dec', api: 'Dec' }
    ];

    // Get months from January to current month
    const monthsUpToCurrent = allMonths.slice(0, currentMonthIndex + 1);

    // Reorder months to show current month first, then previous months in descending order
    const months = [
        monthsUpToCurrent[monthsUpToCurrent.length - 1], // Current month
        ...monthsUpToCurrent.slice(0, -1).reverse() // Previous months in reverse order
    ];

    // 5 fixed cols + monthly cols
    const totalCols = 5 + months.length * 3;

    useEffect(() => {
        const fetchLeaderBoard = async () => {
            try {
                setLoading(true);

                const data = await fetchAllData(
                    'lambdaAPI/Employee/GetLeaderBoardMonthlyHRMHAO'
                );

                if (data && data.status && data.data) {
                    setLeaderData(data.data);
                } else if (Array.isArray(data)) {
                    setLeaderData(data);
                }
            } catch (error) {
                console.error('Error fetching leaderboard data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchLeaderBoard();
    }, []);

    // ─────────────────────────────────────────────
    // Excel Download
    // ─────────────────────────────────────────────

    const handleDownloadExcel = () => {
        const wb = XLSX.utils.book_new();
        const ws = {};

        const setCell = (ws, col, row, value, bold = false) => {
            const ref = XLSX.utils.encode_cell({ c: col, r: row });

            ws[ref] = {
                v: value,
                t: typeof value === 'number' ? 'n' : 's',
                s: {
                    font: { bold },
                    alignment: {
                        horizontal: 'center',
                        vertical: 'center',
                        wrapText: true
                    },
                    border: {
                        top: { style: 'thin', color: { rgb: 'CCCCCC' } },
                        bottom: { style: 'thin', color: { rgb: 'CCCCCC' } },
                        left: { style: 'thin', color: { rgb: 'CCCCCC' } },
                        right: { style: 'thin', color: { rgb: 'CCCCCC' } }
                    },
                    fill: bold
                        ? { fgColor: { rgb: 'F8F9FA' } }
                        : undefined
                }
            };
        };

        // Top Headers
        setCell(ws, 0, 0, 'Sl No', true);
        setCell(ws, 1, 0, 'Name', true);
        setCell(ws, 2, 0, 'Designation', true);
        setCell(ws, 3, 0, 'Supervisor', true);
        setCell(ws, 4, 0, 'Date Of Joining', true);

        let startCol = 5;

        months.forEach((month) => {
            setCell(ws, startCol, 0, month.short, true);
            startCol += 3;
        });

        // Sub Headers
        setCell(ws, 0, 1, '');
        setCell(ws, 1, 1, '');
        setCell(ws, 2, 1, '');
        setCell(ws, 3, 1, '');
        setCell(ws, 4, 1, '');

        startCol = 5;

        months.forEach(() => {
            setCell(ws, startCol, 1, 'Advisors Onboard', true);
            setCell(ws, startCol + 1, 1, 'Advisors Active', true);
            setCell(ws, startCol + 2, 1, 'Activated Accounts', true);

            startCol += 3;
        });

        // Data Rows
        leaderData.forEach((row, index) => {
            const excelRow = index + 2;

            setCell(ws, 0, excelRow, row['Sl No'] ?? index + 1);
            setCell(ws, 1, excelRow, row.Name || '');
            setCell(ws, 2, excelRow, row.Designation || '');
            setCell(ws, 3, excelRow, row.Supervisor || '-');
            setCell(ws, 4, excelRow, row.DateOfJoining || '');

            let col = 5;

            months.forEach((month) => {
                setCell(
                    ws,
                    col,
                    excelRow,
                    row[`${month.api}_AdvisorsOnboard`] ?? 0
                );

                setCell(
                    ws,
                    col + 1,
                    excelRow,
                    row[`${month.api}_AdvisorsActive`] ?? 0
                );

                setCell(
                    ws,
                    col + 2,
                    excelRow,
                    row[`${month.api}_ActivatedAccounts`] ?? 0
                );

                col += 3;
            });
        });

        // Merges
        const merges = [
            { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } },
            { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } },
            { s: { r: 0, c: 2 }, e: { r: 1, c: 2 } },
            { s: { r: 0, c: 3 }, e: { r: 1, c: 3 } },
            { s: { r: 0, c: 4 }, e: { r: 1, c: 4 } }
        ];

        startCol = 5;

        months.forEach(() => {
            merges.push({
                s: { r: 0, c: startCol },
                e: { r: 0, c: startCol + 2 }
            });

            startCol += 3;
        });

        ws['!merges'] = merges;

        // Column Widths
        const cols = [
            { wch: 8 },
            { wch: 24 },
            { wch: 22 },
            { wch: 24 },
            { wch: 18 }
        ];

        months.forEach(() => {
            cols.push(
                { wch: 18 },
                { wch: 18 },
                { wch: 20 }
            );
        });

        ws['!cols'] = cols;

        ws['!rows'] = [{ hpt: 28 }, { hpt: 26 }];

        const lastCol = 5 + months.length * 3 - 1;

        ws['!ref'] = XLSX.utils.encode_range({
            s: { r: 0, c: 0 },
            e: { r: leaderData.length + 1, c: lastCol }
        });

        XLSX.utils.book_append_sheet(wb, ws, 'Monthly Leader Board');

        const fileName = `Monthly_LeaderBoard_${today
            .toISOString()
            .slice(0, 10)}.xlsx`;

        XLSX.writeFile(wb, fileName);
    };

    // ─────────────────────────────────────────────

    const visibleData = leaderData.slice(0, visibleCount);
    const totalCount = leaderData.length;

    const hasMore = visibleCount < totalCount;
    const hasLess = visibleCount > PAGE_SIZE;

    const skeletonDataCols = months.length * 3;

    const skeletonStyles = `
        .lbm-table {
            width: 100%;
            min-width: ${520 + months.length * 180}px;
            border-collapse: collapse;
            font-size: 13px;
        }

        .lbm-table th,
        .lbm-table td {
            border: 1px solid #dee2e6;
            padding: 8px 6px;
            vertical-align: middle;
            text-align: center;
            white-space: nowrap;
        }

        .lbm-left {
            text-align: left !important;
        }

        .lbm-shimmer {
            animation-duration: 2s;
            animation-fill-mode: forwards;
            animation-iteration-count: infinite;
            animation-name: shimmer;
            animation-timing-function: linear;
            background: linear-gradient(
                to right,
                #eeeeee 8%,
                #dddddd 18%,
                #eeeeee 33%
            );
            background-size: 1200px 100%;
            border-radius: 4px;
        }

        @keyframes shimmer {
            0% { background-position: -1200px 0; }
            100% { background-position: 1200px 0; }
        }

        .lbm-zero {
            color: #999;
        }

        .lbm-wrapper {
            overflow-x: auto;
        }
    `;

    const renderValue = (value) => {
        if (value === 0 || value === null || value === undefined) {
            return <span className="lbm-zero">0</span>;
        }

        return value;
    };

    const renderSkeletonRows = () => {
        return [...Array(5)].map((_, index) => (
            <tr key={index}>
                <td>
                    <div
                        className="lbm-shimmer"
                        style={{ height: 14, width: 20 }}
                    ></div>
                </td>

                <td>
                    <div
                        className="lbm-shimmer"
                        style={{ height: 14, width: 120 }}
                    ></div>
                </td>

                <td>
                    <div
                        className="lbm-shimmer"
                        style={{ height: 14, width: 140 }}
                    ></div>
                </td>

                <td>
                    <div
                        className="lbm-shimmer"
                        style={{ height: 14, width: 140 }}
                    ></div>
                </td>

                <td>
                    <div
                        className="lbm-shimmer"
                        style={{ height: 14, width: 90 }}
                    ></div>
                </td>

                {[...Array(skeletonDataCols)].map((__, idx) => (
                    <td key={idx}>
                        <div
                            className="lbm-shimmer"
                            style={{
                                height: 14,
                                width: 30,
                                margin: '0 auto'
                            }}
                        ></div>
                    </td>
                ))}
            </tr>
        ));
    };

    return (
        <div className="card mt-4" style={{ zIndex: 1 }}>
            <style>{skeletonStyles}</style>

            <div className="card-header align-items-center d-flex justify-content-between">
                <h5 className="card-action-title mb-0">
                    Leader Board Monthly - HRM and HAO
                </h5>

                <button
                    className="btn btn-sm btn-outline-success"
                    onClick={handleDownloadExcel}
                    disabled={loading || leaderData.length === 0}
                    title="Download as Excel"
                >
                    <i className="fa-solid fa-file-excel me-1"></i>
                    Download Excel
                </button>
            </div>

            <div className="card-body p-0">
                <div className="lbm-wrapper">
                    <table className="table table-bordered mb-0 lbm-table">
                        <thead>
                            <tr>
                                <th rowSpan="2">Sl No</th>

                                <th rowSpan="2" className="lbm-left">
                                    Name
                                </th>

                                <th rowSpan="2" className="lbm-left">
                                    Designation
                                </th>

                                <th rowSpan="2" className="lbm-left">
                                    Supervisor
                                </th>

                                <th rowSpan="2">
                                    Date Of Joining
                                </th>

                                {months.map((month) => (
                                    <th
                                        key={month.short}
                                        colSpan="3"
                                    >
                                        {month.short}
                                    </th>
                                ))}
                            </tr>

                            <tr>
                                {months.map((month) => (
                                    <React.Fragment key={month.short}>
                                        <th>Advisors Onboard</th>
                                        <th>Advisors Active</th>
                                        <th>Activated Accounts</th>
                                    </React.Fragment>
                                ))}
                            </tr>
                        </thead>

                        <tbody>
                            {loading ? (
                                renderSkeletonRows()
                            ) : leaderData.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={totalCols}
                                        className="text-center py-4 text-muted"
                                    >
                                        No leaderboard data available
                                    </td>
                                </tr>
                            ) : (
                                <>
                                    {visibleData.map((row, index) => (
                                        <tr key={row.EmployeeId || index}>
                                            <td>
                                                {row['Sl No'] ?? index + 1}
                                            </td>

                                            <td className="lbm-left">
                                                {row.Name || '—'}
                                            </td>

                                            <td className="lbm-left">
                                                {row.Designation || '—'}
                                            </td>

                                            <td className="lbm-left">
                                                {row.Supervisor || '-'}
                                            </td>

                                            <td>
                                                {row.DateOfJoining || '—'}
                                            </td>

                                            {months.map((month) => (
                                                <React.Fragment
                                                    key={month.short}
                                                >
                                                    <td>
                                                        {renderValue(
                                                            row[
                                                            `${month.api}_AdvisorsOnboard`
                                                            ]
                                                        )}
                                                    </td>

                                                    <td>
                                                        {renderValue(
                                                            row[
                                                            `${month.api}_AdvisorsActive`
                                                            ]
                                                        )}
                                                    </td>

                                                    <td>
                                                        {renderValue(
                                                            row[
                                                            `${month.api}_ActivatedAccounts`
                                                            ]
                                                        )}
                                                    </td>
                                                </React.Fragment>
                                            ))}
                                        </tr>
                                    ))}

                                    {(hasMore || hasLess) && (
                                        <tr>
                                            <td
                                                colSpan={totalCols}
                                                style={{
                                                    backgroundColor: '#f8f9fa',
                                                    padding: '10px',
                                                    borderTop:
                                                        '2px solid #dee2e6',
                                                    position: 'sticky',
                                                    left: 0,
                                                    textAlign: 'left'
                                                }}
                                            >
                                                {hasMore && (
                                                    <button
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            color: '#4287f5',
                                                            fontSize: '13px',
                                                            fontWeight: 600,
                                                            cursor: 'pointer'
                                                        }}
                                                        onClick={() =>
                                                            setVisibleCount(
                                                                (prev) =>
                                                                    prev +
                                                                    PAGE_SIZE
                                                            )
                                                        }
                                                    >
                                                        <i
                                                            className="fa-solid fa-chevron-down me-1"
                                                            style={{
                                                                fontSize: 11
                                                            }}
                                                        ></i>

                                                        Show more

                                                        <span
                                                            style={{
                                                                fontSize: 12,
                                                                color: '#999',
                                                                marginLeft: 8
                                                            }}
                                                        >
                                                            ({visibleCount} of{' '}
                                                            {totalCount} shown)
                                                        </span>
                                                    </button>
                                                )}

                                                {hasLess && (
                                                    <button
                                                        style={{
                                                            background: 'none',
                                                            border: 'none',
                                                            color: '#888',
                                                            fontSize: '13px',
                                                            fontWeight: 600,
                                                            cursor: 'pointer',
                                                            marginLeft: 16
                                                        }}
                                                        onClick={() =>
                                                            setVisibleCount(
                                                                PAGE_SIZE
                                                            )
                                                        }
                                                    >
                                                        <i
                                                            className="fa-solid fa-chevron-up me-1"
                                                            style={{
                                                                fontSize: 11
                                                            }}
                                                        ></i>

                                                        Show less
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    )}
                                </>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div className="card-footer py-2">
                <small className="text-muted">
                    {!loading && totalCount > 0 && (
                        <span>
                            Total: <strong>{totalCount}</strong> records
                        </span>
                    )}
                </small>
            </div>
        </div>
    );
};

export default DashboardLeaderBoardMonthlyHRMHAO;